import React from "react";
import {
  List,
  Datagrid,
  TextField,
  Show,
  SimpleShowLayout,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateField,
  FileInput,
  FileField,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  required,
  FunctionField,
  NumberField,
  NumberInput,
  TabbedShowLayout,
  SelectField,
  UrlField,
  useNotify,
  useDataProvider,
  TopToolbar,
  EditButton,
  ShowProps,
  Button,
  CreateButton,
  ExportButton,
  FilterButton,
} from "react-admin";

import { useRecordContext, SearchInput } from "react-admin";
import SeverityChipField from "../components/SeverityChipField";
import { ADJUDICATION_CATEGORY_CHOICES, STATUS_CHOICES } from "../consts";
import StringArrayField from "../components/StringArrayField";
import { FileDownload, Sync } from "@mui/icons-material";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import DownloadIcon from "@mui/icons-material/Download";
import {
  SxProps,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";

// Inline custom export button for matched tracked-cves
const escapeCSV = (value: any): string => {
  if (value === null) return "";
  let str = Array.isArray(value) ? value.join(",") : String(value);
  if (
    str.includes(",") ||
    str.includes("\n") ||
    str.includes("\r") ||
    str.includes('"')
  ) {
    const escapedValue = str.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }
  return str;
};
const toCSV = (data: any[]): string => {
  if (!data.length) return "";
  const keys = Object.keys(data[0]);
  const csvRows = [keys.map(escapeCSV).join(",")];
  for (const row of data) {
    csvRows.push(keys.map((k) => escapeCSV(row[k])).join(","));
  }
  return csvRows.join("\n");
};

const MatchedCveExportButton: React.FC<{
  record: any;
  sx?: SxProps;
  disabled?: boolean;
}> = ({ record, sx, disabled = false }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = React.useState(false);
  const handleExport = async () => {
    setLoading(true);
    try {
      const filter = { _id: { $in: record?.trackedCves || [] } };
      const { data } = await dataProvider.getAll("cve/tracked-cves", {
        filter,
      });
      // Remove unwanted keys from all rows
      const filteredData = data.map((row: any) => {
        const newRow: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          if (!["_id", "__v"].includes(key)) {
            newRow[key] = row[key];
          }
        });
        return newRow;
      });
      let csv = toCSV(filteredData);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "matched_tracked_cves.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify("Export complete", {
        type: "success",
        autoHideDuration: 5000,
        container: "center",
      });
    } catch (e) {
      notify("Export failed", {
        type: "error",
        autoHideDuration: 10000,
        container: "center",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      disabled={loading || disabled}
      onClick={handleExport}
      label={loading ? "Exporting..." : "Download Matched"}
      sx={sx}
    >
      <DownloadIcon />
    </Button>
  );
};

const rowShow = () => (
  <SimpleShowLayout>
    <TextField source="description" />
  </SimpleShowLayout>
);

const SbomListHelpDialog: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        label="Help"
        onClick={() => setOpen(true)}
        startIcon={<QuestionMarkIcon />}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <b>Software Bill of Materials (SBOM) Overview</b>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            This page displays all uploaded SBOMs, which are inventories of
            software components and their associated vulnerabilities (CVEs). The
            SBOMs matched CVEs will ONLY contain data for CVEs matching the CVE
            ID, image:tag and release version provided during SBOM creation.
          </Typography>
          <Typography variant="body2" paragraph>
            You can upload, view, re-scan, and export details for each SBOM.
            <ul>
              <li>
                <b>Upload:</b> File must be a CSV format and include one column
                with header "cve" that contains the CVE ID, and one column with
                the header "image" that contains the full image repository name,
                tag, and version. For example:
                <pre>
                  cve,image
                  <br />
                  CVE-2025-32415,quay.io/astronomer/ap-astro-ui:0.37.11
                </pre>
              </li>
              <li>
                <b>View:</b> The SBOM details page lists the total submitted
                CVEs found in the original file as well as the total matched
                CVEs found in the ATLAS tracked vulnerabilities. The "Matched
                CVEs" tab displays the filtered list of matched CVEs in ATLAS.
              </li>
              <br />
              <li>
                <b>Re-scan:</b> The SBOM will always show the most up-to-date
                information regarding matched CVEs, however if new CVEs were
                released after SBOM creation, you will need to "Re-scan" in
                order to potentially find matches that were originally missed.
              </li>
              <br />
              <li>
                <b>Download Original:</b> Download the original file that was
                uploaded to ATLAS on SBOM creation.
              </li>
              <br />
              <li>
                <b>Download Matched:</b> Download only the ATLAS tracked CVEs
                that matched the submitted CVEs. This will contain all ATLAS
                data for each CVE.
              </li>
              <br />
              <li>
                <b>Download Combined:</b> Download the combined CSV that
                contains all rows and fields from the original file as well as
                supplemental columns containins matched CVE information. This is
                typically the file you would want to share with a customer
                regarding SBOM results.
              </li>
            </ul>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <em>
              Tip: For broader filtering capabilties, visit the "Tracked CVEs"
              page where you can filter by release version, status, severity and
              many other fields as well as customize the exported data by
              selected columns.
            </em>
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SbomListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportButton />
    <SbomListHelpDialog />
  </TopToolbar>
);

const sbomFilters = [
  <SearchInput source="q_name_originalFileName" alwaysOn key="q" />,
  <ReferenceInput
    source="release"
    reference="cve/releases"
    sort={{ field: "version", order: "DESC" }}
  >
    <AutocompleteInput
      optionText={(option) => `${option.name}:${option.version}`}
      style={{ width: "20em" }}
      filterToQuery={(value) => ({ q_name_version: value })}
    />
  </ReferenceInput>,
];

export const SbomList = (props: any) => (
  <List {...props} actions={<SbomListActions />} filters={sbomFilters}>
    <Datagrid rowClick="show" expand={rowShow}>
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="originalFileName" />
      <NumberField source="totalSubmittedCves" />
      <NumberField source="totalMatchedCves" />
      <DateField source="createdAt" />
    </Datagrid>
  </List>
);

const SbomShowActions = () => {
  const dataProvider = useDataProvider();
  const record = useRecordContext();
  const notify = useNotify();
  const [, setRefresh] = React.useState(0); // force refresh
  const [loading, setLoading] = React.useState(false);

  const handleRescan = async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      // PUT to sboms/:id/rescan
      const response = await dataProvider.custom(
        `cve/sboms/${record.id}/rescan`,
        "PUT"
      );
      const newData = response?.data;
      if (newData) {
        const before = record.totalMatchedCves ?? 0;
        const after = newData.totalMatchedCves ?? 0;
        const diff = after - before;
        notify(
          `Re-scan complete. Matched CVEs with ${diff} changes (${before} → ${after}).`,
          { type: "success" }
        );
        // Update record context (force refresh)
        Object.assign(record, newData);
        setRefresh((r) => r + 1);
      } else {
        notify("Re-scan complete, but no data returned.", { type: "warning" });
      }
    } catch (error) {
      notify(`Re-scan failed: ${error}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (type: string) => {
    if (!record?.id) return;
    setLoading(true);
    try {
      // Call the backend custom endpoint
      const response = await dataProvider.custom(
        `cve/sboms/${record.id}/downloadUrl?type=${type}`,
        "GET"
      );
      const signedUrl = response?.data;
      if (signedUrl) {
        // Determine filename based on type
        let filename = "download.csv";
        if (type === "original") {
          filename = record.originalFileName || "original.csv";
        } else if (type === "latestCombined") {
          filename = record.combinedFileName || "combined.csv";
        }
        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = signedUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      notify(`Download failed: ${error}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TopToolbar>
      <EditButton />
      <Button
        label="Re-Scan"
        onClick={handleRescan}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        <Sync />
      </Button>
      <Button
        label="Download Original"
        onClick={() => handleDownload("original")}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        <FileDownload />
      </Button>
      {/* Custom Export Button for Download Matched CVEs */}
      <MatchedCveExportButton
        record={record}
        sx={{ ml: 1 }}
        disabled={loading}
      />
      <Button
        label="Download Combined"
        onClick={() => handleDownload("latestCombined")}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        <FileDownload />
      </Button>
    </TopToolbar>
  );
};

export const SbomShow = (props: ShowProps & { actions?: React.ReactNode }) => {
  const { actions = <SbomShowActions /> } = props;
  return (
    <Show {...props} actions={actions}>
      <TabbedShowLayout
        sx={{
          "& .RaTabbedShowLayout-content": {
            padding: 0,
          },
        }}
      >
        <TabbedShowLayout.Tab label="Details">
          <SimpleShowLayout>
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="description" sx={{ whiteSpace: 'pre-wrap' }} />
            <ReferenceField source="release" reference="cve/releases">
              <FunctionField
                source="name"
                render={(record) => `${record.name}:${record.version}`}
              />
            </ReferenceField>
            <TextField source="originalFileName" />
            <NumberField source="totalSubmittedCves" />
            <NumberField source="totalMatchedCves" />
            <DateField source="createdAt" />
          </SimpleShowLayout>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Matched CVEs">
          <MatchedCveList />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Un-Matched CVEs">
          <UnmatchedCveList />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const UnmatchedCveList = () => {
  const record = useRecordContext();
  if (
    !record ||
    !Array.isArray(record.unMatchedCves) ||
    record.unMatchedCves.length === 0
  ) {
    return <div style={{ padding: "1em" }}>No unmatched CVEs.</div>;
  }
  return (
    <Datagrid
      data={record.unMatchedCves}
      total={record.unMatchedCves.length}
      bulkActionButtons={false}
      rowClick={false}
    >
      <TextField source="cve" label="CVE" />
      <TextField source="image" />
      <TextField source="reason" />
    </Datagrid>
  );
};

const MatchedCveList = () => {
  const record = useRecordContext();
  if (
    !record ||
    !Array.isArray(record.trackedCves) ||
    record.trackedCves.length === 0
  ) {
    return <div style={{ padding: "1em" }}>No matched CVEs.</div>;
  }
  return (
    <List
      disableSyncWithLocation
      resource={`cve/sboms/${record.id}/matchedCves`}
      actions={false}
      title={false}
    >
      <Datagrid rowClick="show" expand={<MatchedCveRowExpand />}>
        <TextField source="name" />
        <SeverityChipField source="severity" />
        <SelectField source="status" choices={STATUS_CHOICES} />
        <TextField source="imageRepository" />
        <TextField source="imageTag" />
        <TextField source="availableFixedImageBuildTag" label="Fixed Tag" />
        <TextField
          source="availableFixedReleaseVersion"
          label="Fixed Release"
        />
        <TextField source="targetFixReleaseDate" label="Target Date" />
        <SelectField
          source="adjudicationCategory"
          choices={ADJUDICATION_CATEGORY_CHOICES}
        />
        <DateField source="createdAt" />
      </Datagrid>
    </List>
  );
};

const MatchedCveRowExpand = () => (
  <SimpleShowLayout>
    <TextField source="description" />
    <TextField source="imageRepository" />
    <TextField source="imageTag" />
    <TextField source="availableFixedImageBuildTag" />
    <TextField source="availableFixedReleaseVersion" />
    <NumberField source="companyScore" />
    <NumberField source="cvssScore" />
    <NumberField source="epssScore" />
    <TextField source="vulnerableDependencyName" />
    <TextField source="vulnerableDependencyPackageName" />
    <TextField source="vulnerableDependencyVersion" />
    <StringArrayField source="vulnerableDependencyFixVersions" />
    <StringArrayField source="vulnerableDependencyPaths" />
    <UrlField source="githubIssue" />
    <UrlField source="jiraTicket" />
    <SelectField
      source="adjudicationCategory"
      choices={ADJUDICATION_CATEGORY_CHOICES}
    />
    <TextField source="adjudicationNotes" />
    <DateField source="createdAt" />
    <DateField source="updatedAt" />
  </SimpleShowLayout>
);

export const SbomEdit = (props: any) => (
  <Edit {...props} redirect="show">
    <SimpleForm>
      <TextInput source="name" />
      <TextInput source="description" />
      <ReferenceInput source="release" reference="cve/releases">
        <AutocompleteInput
          optionText={(option: { name: string; version: string }) =>
            `${option.name}:${option.version}`
          }
          disabled
        />
      </ReferenceInput>
      <TextInput source="originalFileName" disabled />
      <NumberInput source="totalSubmittedCves" disabled />
      <NumberInput source="totalMatchedCves" disabled />
    </SimpleForm>
  </Edit>
);

export const SbomCreate = (props: any) => (
  <Create {...props} redirect={"show"}>
    <SimpleForm>
      <TextInput source="name" validate={[required()]} />
      <TextInput source="description" validate={[required()]} />
      <ReferenceInput
        source="release"
        reference="cve/releases"
        sort={{ field: "version", order: "DESC" }}
      >
        <AutocompleteInput
          optionText={(option: { name: string; version: string }) =>
            `${option.name}:${option.version}`
          }
          validate={[required()]}
          filterToQuery={(value) => ({
            q_name_version: value,
          })}
        />
      </ReferenceInput>
      <FileInput
        source="uploadedFile"
        label="CSV"
        accept={{ "text/csv": [".csv"] }}
        multiple={false}
      >
        <FileField source="src" title="title" />
      </FileInput>
    </SimpleForm>
  </Create>
);
